import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, LayoutDashboard, Globe, Building2, Factory, FileText, Bell, TrendingUp, Palette, Upload, Database, Mail, Keyboard } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';

const sections = [
  {
    id: 'getting-started',
    title: 'Getting started',
    icon: LayoutDashboard,
    content: [
      { label: 'Dashboard', text: 'Your central command centre. See overall risk scores, active alerts, priority country cards, sector risk charts, and AI-generated briefings — all personalised to your audience type and monitored countries.' },
      { label: 'Countries', text: 'Drill into any country for a detailed risk profile including a radar chart, AI-generated narrative, trade data, currency movements, active hazards, and freight indicators.' },
      { label: 'Sectors', text: 'View risk scores across key sectors like mining, agribusiness, and defence. Click through for sector-specific signals and country exposure breakdowns.' },
      { label: 'Suppliers', text: 'Upload your supplier list via CSV or add them manually. Each supplier is scored against country, sector, currency, disaster, and freight risk — giving you a personalised view of your supply chain exposure.' },
      { label: 'Reports', text: 'Access 17 AI-generated risk reports including weekly summaries, country deep dives, and audience-specific reports. Download any report as a professional PDF.' },
      { label: 'Alerts', text: 'Configure risk alerts with custom triggers, country filters, and severity thresholds. Alerts are pre-loaded with audience-specific defaults when you first visit.' },
      { label: 'Trends', text: 'Compare historical risk scores across multiple countries or deep-dive into a single country\'s risk breakdown over 6, 12, or 24 months.' },
    ],
  },
  {
    id: 'risk-scores',
    title: 'Understanding risk scores',
    icon: TrendingUp,
    content: [
      { label: 'Scale', text: 'Every country, sector, and supplier is scored on a 0-100 scale. Higher scores indicate greater risk to Australian supply chains.' },
      { label: 'Low (0-39)', text: 'Green — Stable conditions. No immediate action required. Continue routine monitoring.', color: '#2E7D32' },
      { label: 'Moderate (40-64)', text: 'Amber — Elevated risk signals detected. Review exposure and update contingency plans.', color: '#FF9500' },
      { label: 'High (65-84)', text: 'Red — Significant risk. Active management required. Consider alternative sourcing or accelerated stock building.', color: '#FF3B30' },
      { label: 'Critical (85-100)', text: 'Dark red — Severe disruption likely. Immediate action required. Activate business continuity plans.', color: '#8B0000' },
      { label: 'Data inputs', text: 'Scores are calculated from 7 live data feeds: GDELT geopolitical signals, GNews sector news, World Bank & IMF economic indicators, UN Comtrade bilateral trade stats, Open Exchange Rates currency data, and GDACS natural disaster alerts.' },
    ],
  },
  {
    id: 'uploading-suppliers',
    title: 'Uploading suppliers',
    icon: Upload,
    content: [
      { label: 'Step 1', text: 'Navigate to the Suppliers page from the top navigation bar.' },
      { label: 'Step 2', text: 'Click "Upload CSV" or download the CSV template first to see the expected format.' },
      { label: 'Step 3', text: 'Your file should include columns: Supplier name (required), Country (required), City/region, Primary sector, Commodities, HS code, Annual spend (AUD), and Tier.' },
      { label: 'Step 4', text: 'Upload your file. TradePath will parse it, calculate risk scores for each supplier, and show a preview before confirming.' },
      { label: 'Step 5', text: 'Once confirmed, each supplier appears in your list with a composite risk score based on their country (40%), sector (15%), currency (15%), disaster (15%), and freight (15%) risk.' },
      { label: 'Tip', text: 'You can also add individual suppliers manually using the "Add" button.' },
    ],
  },
  {
    id: 'data-sources',
    title: 'Data sources',
    icon: Database,
    content: [
      { label: 'GDELT', text: 'Global Database of Events, Language, and Tone — monitors geopolitical events and media sentiment in real time.' },
      { label: 'GNews', text: 'Aggregates sector-specific news articles globally for signal detection and trend analysis.' },
      { label: 'World Bank & IMF', text: 'Economic indicators including GDP growth, inflation, trade openness, and governance metrics.' },
      { label: 'UN Comtrade', text: 'Official bilateral trade statistics between your home country and trading partners.' },
      { label: 'Open Exchange Rates', text: 'Live and historical currency exchange rates across all major trading currencies.' },
      { label: 'GDACS', text: 'Global Disaster Alerting Coordination System — real-time earthquake, cyclone, flood, volcano, and wildfire alerts.' },
      { label: 'Claude AI', text: 'Generates risk narratives, briefings, and country analysis summaries from aggregated data.' },
    ],
  },
  {
    id: 'personalising',
    title: 'Personalising your dashboard',
    icon: Palette,
    content: [
      { label: 'Onboarding', text: 'When you first visit, complete the onboarding flow to select your audience type (Importer, Government, or Defence), monitored countries, and priority sectors.' },
      { label: 'Settings', text: 'Navigate to Settings (gear icon in the top bar) to update your company name, primary role, monitored countries, and risk category preferences at any time.' },
      { label: 'What changes', text: 'Your audience type determines: dashboard banner content, priority signal ordering, report templates, default alerts, and contextual banners (import cost estimates, compliance snapshots, or sovereignty scores).' },
    ],
  },
  {
    id: 'contact',
    title: 'Contact support',
    icon: Mail,
    content: [
      { label: 'Email', text: 'support@nuvantasolutions.com.au', isLink: true },
      { label: 'Response time', text: 'We aim to respond to all support enquiries within one business day.' },
    ],
  },
  {
    id: 'shortcuts',
    title: 'Keyboard shortcuts',
    icon: Keyboard,
    content: [
      { label: '?', text: 'Open / close this help panel' },
      { label: 'Esc', text: 'Close any open dialog or panel' },
    ],
  },
];

export const HelpPanel = ({ open, onClose }) => {
  const [expandedSection, setExpandedSection] = useState('getting-started');

  return (
    <AnimatePresence>
      {open && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/30 z-[60]"
            onClick={onClose}
            data-testid="help-panel-backdrop"
          />
          {/* Panel */}
          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 28, stiffness: 300 }}
            className="fixed right-0 top-0 bottom-0 w-full max-w-[420px] bg-white shadow-[-8px_0_30px_rgba(0,0,0,0.12)] z-[70] flex flex-col"
            data-testid="help-panel"
          >
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-[#E0E0E0] bg-[#0A2533]">
              <div>
                <h2 className="text-lg font-bold text-white">Help & Guide</h2>
                <p className="text-xs text-white/60 mt-0.5">Nuvanta TradePath</p>
              </div>
              <button
                onClick={onClose}
                className="text-white/70 hover:text-white transition-colors p-1 rounded-lg hover:bg-white/10"
                data-testid="help-panel-close"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Content */}
            <ScrollArea className="flex-1">
              <div className="p-5 space-y-1">
                {sections.map((section) => {
                  const Icon = section.icon;
                  const isExpanded = expandedSection === section.id;
                  return (
                    <div key={section.id}>
                      <button
                        className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition-colors ${isExpanded ? 'bg-[#E0F7FA] text-[#0097A7]' : 'text-[#0D3B4F] hover:bg-[#F5F5F5]'}`}
                        onClick={() => setExpandedSection(isExpanded ? null : section.id)}
                        data-testid={`help-section-${section.id}`}
                      >
                        <Icon className={`h-4 w-4 flex-shrink-0 ${isExpanded ? 'text-[#0097A7]' : 'text-[#4E6974]'}`} />
                        <span className="text-sm font-semibold">{section.title}</span>
                      </button>
                      <AnimatePresence>
                        {isExpanded && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: 'auto', opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.2 }}
                            className="overflow-hidden"
                          >
                            <div className="pl-10 pr-3 pb-3 space-y-3">
                              {section.content.map((item, i) => (
                                <div key={i} className="text-sm">
                                  <span className="font-medium text-[#0D3B4F]" style={item.color ? { color: item.color } : undefined}>
                                    {item.label}
                                  </span>
                                  {item.isLink ? (
                                    <a href={`mailto:${item.text}`} className="ml-1 text-[#0097A7] hover:underline">{item.text}</a>
                                  ) : (
                                    <p className="text-[#4E6974] mt-0.5 leading-relaxed">{item.text}</p>
                                  )}
                                </div>
                              ))}
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                      {section.id !== 'shortcuts' && <Separator className="my-0.5" />}
                    </div>
                  );
                })}
              </div>
            </ScrollArea>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default HelpPanel;
